import type { WorkflowConfig } from "../../api/workflow";
import { displayDepartment } from "../../api/departments";
import {
  humanizeStatus,
  statusCategory,
  statusLabel,
  statusShortLabel,
} from "../../hooks/useWorkflow";
import {
  REWARD_CATEGORIES,
  resolveSuggestionUnit,
  suggestionEvaluations,
  type Suggestion,
  type SuggestionListFilters,
  type SuggestionStatus,
} from "../../types/ess";
import { slaState } from "../../utils/sla";
import {
  bucketRange,
  pickTrendGranularity,
  type TrendBucket,
  type TrendGranularity,
} from "../../utils/bucket";

export type StatusFilter = SuggestionStatus[];

export interface SuggestionsUrlParts {
  status?: StatusFilter;
  category?: string[];
  department?: string[];
  // Unit + reward-band facets — same client-side multi-select family as
  // status/category/department (see SuggestionListFilters, types/ess.ts).
  unit?: string[];
  reward_category?: string[];
  date_from?: string;
  date_to?: string;
  /**
   * Source label that the user clicked — e.g. "Passed SC", "By department · PP",
   * "Closed". AppLayout reads this back as the AppBar title so the drilled-in
   * page reflects the affordance the user came from. Optional — fallback is
   * "Suggestions".
   */
  label?: string;
  /**
   * Drill-downs that need a *non-server-expressible* filter (e.g. "stale
   * for >14d on updated_at") set their own marker here. Landing.tsx
   * applies the matching client-side predicate. Keep tightly enumerated.
   */
  facet?: "aging" | "sla_breached";
  /**
   * Pipeline stage the drill-down counts reach of. Its status filter is a
   * reachable set spanning several groups, so Landing shows one
   * "Reached: <stage>" chip instead of a chip per state.
   */
  reached?: string;
}

export function buildSuggestionsUrl(parts: SuggestionsUrlParts): string {
  const sp = new URLSearchParams();
  const push = (key: string, v?: string[] | string) => {
    if (Array.isArray(v)) {
      if (v.length) sp.set(key, v.join(","));
    } else if (v) {
      sp.set(key, v);
    }
  };
  push("status", parts.status);
  push("category", parts.category);
  push("department", parts.department);
  push("unit", parts.unit);
  push("reward_category", parts.reward_category);
  push("date_from", parts.date_from);
  push("date_to", parts.date_to);
  if (parts.label) sp.set("label", parts.label);
  if (parts.facet) sp.set("facet", parts.facet);
  if (parts.reached) sp.set("reached", parts.reached);
  // Mark this as a drill-down from Overview so AppLayout surfaces a back
  // arrow. Landing.tsx preserves this param across its filter-URL rewrite.
  sp.set("from", "overview");
  const qs = sp.toString();
  return qs ? `/suggestions?${qs}` : "/suggestions";
}

export function priorPeriod(
  date_from?: string,
  date_to?: string,
): { date_from: string; date_to: string } | null {
  if (!date_from || !date_to) return null;
  const from = new Date(date_from);
  const to = new Date(date_to);
  const ms = to.getTime() - from.getTime();
  if (ms <= 0) return null;
  const priorTo = new Date(from);
  const priorFrom = new Date(from.getTime() - ms);
  return {
    date_from: priorFrom.toISOString(),
    date_to: priorTo.toISOString(),
  };
}

// Aging threshold for the "stuck in pipeline" meta tile — rows whose
// last status change is older than this count toward `agingCount`.
export const AGING_CUTOFF_MS = 14 * 86400000;

// Filter-narrowing helper used by Reports's stats query.
export function singleDepartment(
  filters: SuggestionListFilters,
): string | undefined {
  return filters.department && filters.department.length === 1
    ? filters.department[0]
    : undefined;
}

/**
 * Build `state -> Set<reachable states>` from the workflow's action graph.
 *
 * Used by the Overview Pipeline so a stage's cumulative-reach count
 * includes rejected branches that originate downstream of it. A row
 * currently in `rejected_by_wc` is reachable from `submitted` and
 * `accepted_by_hr`, but NOT from `accepted_by_wc` — so it counts toward
 * the first two stages and not the third, matching the actual lifecycle.
 *
 * Self-transitions (e.g. `assign_reviewers` whose next_state equals the
 * current state) and any other cycles are handled by the visited set.
 * Returns `{}` for an undefined or stateless workflow.
 *
 * States no action leads to (`rejected_by_hr`, legacy `under_verification`)
 * still hold rows, so the graph alone would drop them from every stage:
 * - every state is reached from `initial_state` — every row started there;
 * - such an orphan also counts toward every state that reaches all of its
 *   successors without being one of them (`under_verification` ->
 *   `implementation_closed` counts toward `implemented` and everything
 *   before it, not toward Closed). A terminal orphan has no successors to
 *   place it by, so it stays at the initial state only.
 */
export function buildReachableStates(
  workflow: WorkflowConfig | undefined,
): Record<string, Set<string>> {
  const states = workflow?.states ?? {};
  const adjacency: Record<string, Set<string>> = {};
  for (const [name, def] of Object.entries(states)) {
    const out = new Set<string>();
    for (const a of def.actions ?? []) {
      if (a.next_state) out.add(a.next_state);
    }
    adjacency[name] = out;
  }
  const result: Record<string, Set<string>> = {};
  for (const root of Object.keys(adjacency)) {
    const visited = new Set<string>([root]);
    const stack: string[] = [root];
    while (stack.length) {
      const node = stack.pop() as string;
      for (const next of adjacency[node] ?? []) {
        if (!visited.has(next)) {
          visited.add(next);
          stack.push(next);
        }
      }
    }
    result[root] = visited;
  }

  const initial = workflow?.initial_state;
  if (initial && result[initial]) {
    Object.keys(adjacency).forEach((name) => result[initial].add(name));
  }

  const incoming = new Set<string>();
  for (const [name, out] of Object.entries(adjacency)) {
    out.forEach((next) => {
      if (next !== name) incoming.add(next);
    });
  }
  for (const orphan of Object.keys(adjacency)) {
    if (orphan === initial || incoming.has(orphan)) continue;
    const successors = Array.from(adjacency[orphan]).filter((n) => n !== orphan);
    if (successors.length === 0) continue;
    for (const [root, reach] of Object.entries(result)) {
      if (root === orphan || successors.includes(root)) continue;
      if (successors.every((n) => reach.has(n))) reach.add(orphan);
    }
  }
  return result;
}

// ─── Report model ─────────────────────────────────────────────────────
// One aggregation over the filtered row set, shared by the Overview
// (Reports.tsx), its XLSX export and the print route (OverviewPrint.tsx),
// so the three can never disagree about a count. Every input is injected
// (rows, workflow, unit lookup, clock); nothing here reads a hook.

// Yaml category vocabulary the report partitions states by. Categories are
// a small, stable contract — a new state inside an existing category does
// not change these sets.
const CLOSED_CATEGORIES = new Set(["completed", "done", "closed"]);
const TERMINAL_CATEGORIES = new Set(["completed", "done", "closed", "rejected"]);

export interface ReportStateSets {
  happyPath: string[];
  initialState?: string;
  closedStates: string[];
  rejectedStates: string[];
  nonTerminalStates: string[];
}

export function buildStateSets(
  workflow: WorkflowConfig | undefined,
): ReportStateSets {
  const states = Object.entries(workflow?.states ?? {});
  return {
    happyPath: workflow?.happy_path ?? [],
    initialState: workflow?.initial_state,
    closedStates: states
      .filter(([, s]) => CLOSED_CATEGORIES.has(s.category))
      .map(([name]) => name),
    rejectedStates: states
      .filter(([, s]) => s.category === "rejected")
      .map(([name]) => name),
    nonTerminalStates: states
      .filter(([, s]) => !TERMINAL_CATEGORIES.has(s.category))
      .map(([name]) => name),
  };
}

/**
 * The committee-report buckets, in reading order. These labels are the
 * report vocabulary on screen (Committee Report), in the XLSX and in print;
 * pipeline stages and status chips keep the yaml's own names.
 *
 * - received — every row in scope.
 * - recommendedByWc — the row's status is reachable from `accepted_by_wc`:
 *   the WC forwarded it, whatever happened at SC afterwards.
 * - rejectedByWc / rejectedBySc — the row sits in `rejected_by_wc` /
 *   `rejected_by_sc` (named terminal outcomes, not a reachable range).
 * - approvedBySc — reachable from `accepted_by_sc`.
 * - implemented — reachable from `implemented`, so closed rows and legacy
 *   `under_verification` rows (implemented, awaiting the old verification
 *   leg) count too.
 * - closed — the row is in a closed-category state (`implementation_closed`),
 *   the same population as the Overview's closure rate.
 */
export const REPORT_BUCKETS = [
  { key: "received", label: "Received" },
  { key: "recommendedByWc", label: "Recommended by WC" },
  { key: "rejectedByWc", label: "Rejected by WC" },
  { key: "approvedBySc", label: "Approved by SC" },
  { key: "rejectedBySc", label: "Rejected by SC" },
  { key: "implemented", label: "Implemented" },
  { key: "closed", label: "Closed" },
] as const;

export type ReportBucketKey = (typeof REPORT_BUCKETS)[number]["key"];

export interface ReportPredicates {
  recommendedByWc: (s: Suggestion) => boolean;
  approvedBySc: (s: Suggestion) => boolean;
  implemented: (s: Suggestion) => boolean;
  closed: (s: Suggestion) => boolean;
  categoryOf: (status: string) => string;
}

/**
 * Committee milestones are rooted by literal state name (they are named
 * steps of the WC/SC procedure, not yaml categories). A workflow that lacks
 * one falls back to an exact-status match.
 */
export function buildReportPredicates(
  workflow: WorkflowConfig | undefined,
  reachable: Record<string, Set<string>> = buildReachableStates(workflow),
): ReportPredicates {
  const reached = (root: string) => {
    const set = reachable[root] ?? new Set([root]);
    return (s: Suggestion) => set.has(s.status);
  };
  const categoryOf = (status: string) => statusCategory(workflow, status);
  return {
    recommendedByWc: reached("accepted_by_wc"),
    approvedBySc: reached("accepted_by_sc"),
    implemented: reached("implemented"),
    closed: (s) => CLOSED_CATEGORIES.has(categoryOf(s.status)),
    categoryOf,
  };
}

// Unit bucket for an approved row that resolves to no unit at all
// (resolveSuggestionUnit returns undefined) — legacy rows with neither
// `metadata.unit` nor a matching `unitByDepartment` entry. Kept visible
// rather than silently dropped, so the report accounts for every
// approved row.
const UNKNOWN_UNIT = "Unknown";

// `topDepartments` answers a single question — which department sent in
// the most suggestions — so it ships as a short ranking rather than a
// full leaderboard. Capped here instead of at each caller because the
// Committee Report, the XLSX export and print all want the same handful.
const TOP_DEPARTMENT_LIMIT = 5;

const SC_OUTCOME_LABELS: Record<string, string> = {
  approved: "Approved",
  rejected: "Rejected",
  in_process: "In Process",
};

/** One row of the Steering Committee evaluation table (2024 SC report layout). */
export interface ScEvaluationRow {
  id: string;
  suggestionNumber: string;
  title: string;
  originator: string;
  employeeId: string;
  department: string;
  /** SC total, out of 100 — i.e. already a percentage. */
  rating: number;
  rewardCategory: string;
  remark: string;
}

/**
 * Rows with a recorded SC evaluation, highest rating first, then by
 * suggestion number (numbers compare numerically; rows without one last).
 * The remark is the SC's comment, or the outcome label when it left none.
 */
export function buildScEvaluationRows(rows: Suggestion[]): ScEvaluationRow[] {
  const out: ScEvaluationRow[] = [];
  for (const s of rows) {
    const ssc = suggestionEvaluations(s).ssc;
    if (!ssc || typeof ssc.total !== "number") continue;
    const comment = (ssc.comment ?? "").trim();
    out.push({
      id: s.id,
      suggestionNumber: s.suggestion_number ?? "",
      title: s.title,
      originator: s.employee_name,
      employeeId: s.employee_id,
      department: displayDepartment(s.department),
      rating: Math.round(ssc.total),
      rewardCategory: ssc.reward_category ?? "",
      remark:
        comment ||
        SC_OUTCOME_LABELS[ssc.outcome] ||
        (ssc.outcome ? humanizeStatus(ssc.outcome) : ""),
    });
  }
  return out.sort(
    (a, b) =>
      b.rating - a.rating ||
      (a.suggestionNumber ? 0 : 1) - (b.suggestionNumber ? 0 : 1) ||
      a.suggestionNumber.localeCompare(b.suggestionNumber, undefined, {
        numeric: true,
      }) ||
      a.id.localeCompare(b.id),
  );
}

export interface AnnualSscSummary {
  buckets: Record<ReportBucketKey, number>;
  byRewardCategory: Record<string, number>; // A..F
  /** Every SC-evaluated row, best rating first — the "Best suggestions" board. */
  scEvaluations: ScEvaluationRow[];
  /** Approved-by-SC count per person, desc. */
  mostApprovedPerPerson: { name: string; employeeId: string; approved: number }[];
  topUnits: { unit: string; approved: number }[]; // "top plant"
  topDepartments: { department: string; count: number }[]; // most received, desc
  slaBreaches: number;
}

/**
 * Pure aggregator over the already-filtered row set; bucket semantics are
 * documented on REPORT_BUCKETS.
 *
 * Person identity is `employee_id` (grouping key) + `employee_name`
 * (display). `mostApprovedPerPerson` and `topUnits` are built only from
 * rows the SC approved (a person/unit with nothing approved has nothing to
 * rank). Both are returned in full, sorted desc — the screen caps its
 * leaderboards, the XLSX writes every row.
 *
 * `topDepartments` ranks departments by suggestions *received* — every row
 * counts, including rejected ones — which is what makes it a different
 * question from `topUnits`. It is the one array returned pre-capped, at
 * TOP_DEPARTMENT_LIMIT.
 */
export function buildAnnualSscSummary(
  rows: Suggestion[],
  predicates: ReportPredicates,
  unitByDepartment: Record<string, string>,
  now: Date = new Date(),
): AnnualSscSummary {
  const byRewardCategory: Record<string, number> = {};
  for (const cat of REWARD_CATEGORIES) byRewardCategory[cat] = 0;

  const buckets: Record<ReportBucketKey, number> = {
    received: rows.length,
    recommendedByWc: 0,
    rejectedByWc: 0,
    approvedBySc: 0,
    rejectedBySc: 0,
    implemented: 0,
    closed: 0,
  };
  let slaBreaches = 0;

  const approvedByPerson = new Map<string, { name: string; approved: number }>();
  const approvedByUnit = new Map<string, number>();
  const receivedByDepartment = new Map<string, number>();

  for (const s of rows) {
    if (s.status === "rejected_by_wc") buckets.rejectedByWc++;
    if (s.status === "rejected_by_sc") buckets.rejectedBySc++;
    if (predicates.recommendedByWc(s)) buckets.recommendedByWc++;
    if (predicates.implemented(s)) buckets.implemented++;
    if (predicates.closed(s)) buckets.closed++;

    const rewardCategory = suggestionEvaluations(s).ssc?.reward_category;
    if (rewardCategory && rewardCategory in byRewardCategory) {
      byRewardCategory[rewardCategory]++;
    }

    if (slaState(s, predicates.categoryOf, now).breached) slaBreaches++;

    receivedByDepartment.set(
      s.department,
      (receivedByDepartment.get(s.department) ?? 0) + 1,
    );

    if (predicates.approvedBySc(s)) {
      buckets.approvedBySc++;

      const entry =
        approvedByPerson.get(s.employee_id) ?? { name: s.employee_name, approved: 0 };
      entry.approved++;
      approvedByPerson.set(s.employee_id, entry);

      const unit = resolveSuggestionUnit(s, unitByDepartment) ?? UNKNOWN_UNIT;
      approvedByUnit.set(unit, (approvedByUnit.get(unit) ?? 0) + 1);
    }
  }

  const mostApprovedPerPerson = Array.from(approvedByPerson.entries())
    .map(([employeeId, v]) => ({ employeeId, name: v.name, approved: v.approved }))
    .sort((a, b) => b.approved - a.approved || a.name.localeCompare(b.name));

  const topUnits = Array.from(approvedByUnit.entries())
    .map(([unit, approved]) => ({ unit, approved }))
    .sort((a, b) => b.approved - a.approved || a.unit.localeCompare(b.unit));

  const topDepartments = Array.from(receivedByDepartment.entries())
    .map(([department, count]) => ({ department, count }))
    .sort(
      (a, b) => b.count - a.count || a.department.localeCompare(b.department),
    )
    .slice(0, TOP_DEPARTMENT_LIMIT);

  return {
    buckets,
    byRewardCategory,
    scEvaluations: buildScEvaluationRows(rows),
    mostApprovedPerPerson,
    topUnits,
    topDepartments,
    slaBreaches,
  };
}

// ─── Overview report ──────────────────────────────────────────────────

export interface ReportPipelineStage {
  state: string;
  label: string;
  count: number;
  /** Every status that counts toward this stage — the drill-down's filter. */
  reach: string[];
  terminal: boolean;
}

export interface OverviewReport {
  total: number;
  pendingReview: number;
  closed: number;
  rejected: number;
  closureRate: number;
  rejectRate: number;
  medianCloseDays: number | null;
  agingCount: number;
  stateSets: ReportStateSets;
  pipeline: ReportPipelineStage[];
  rejections: { status: string; label: string; count: number }[];
  byStatus: { status: string; label: string; count: number }[];
  byCategory: [string, number][];
  byDepartment: [string, number][];
  /** Head of `byDepartment` — the department that sent in the most. */
  topDepartment?: [string, number];
  byReward: { key: string; count: number }[];
  /** Denominator for the reward bars: the rewarded population. */
  rewardTotal: number;
  committee: AnnualSscSummary;
}

function countBy(rows: Suggestion[], key: (s: Suggestion) => string) {
  const counts = new Map<string, number>();
  for (const r of rows) counts.set(key(r), (counts.get(key(r)) ?? 0) + 1);
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
}

/**
 * Everything the Overview states about a filtered row set. The screen, the
 * XLSX and the print route all read this one object.
 *
 * Pipeline stages follow the yaml `happy_path`; each counts the rows whose
 * current status is reachable from the stage (see buildReachableStates), so
 * a row rejected at WC still counts toward the stages it passed.
 */
export function buildOverviewReport(
  rows: Suggestion[],
  workflow: WorkflowConfig | undefined,
  unitByDepartment: Record<string, string>,
  now: Date = new Date(),
): OverviewReport {
  const stateSets = buildStateSets(workflow);
  const reachable = buildReachableStates(workflow);
  const statusCounts = new Map<string, number>();
  for (const r of rows) {
    statusCounts.set(r.status, (statusCounts.get(r.status) ?? 0) + 1);
  }
  const byStatusCount = (s: string) => statusCounts.get(s) ?? 0;
  const sum = (states: string[]) =>
    states.reduce((acc, s) => acc + byStatusCount(s), 0);
  const shortLabel = (s: string) =>
    statusShortLabel(workflow, s) || statusLabel(workflow, s) || s;

  const total = rows.length;
  const closed = sum(stateSets.closedStates);
  const rejected = sum(stateSets.rejectedStates);

  const closedSet = new Set(stateSets.closedStates);
  const closeDays = rows
    .filter((r) => closedSet.has(r.status))
    .map((r) => {
      const start = new Date(r.created_at).getTime();
      const end = new Date(r.updated_at ?? r.created_at).getTime();
      return Math.max(0, (end - start) / 86400000);
    })
    .sort((a, b) => a - b);
  const mid = Math.floor(closeDays.length / 2);
  const medianCloseDays =
    closeDays.length === 0
      ? null
      : Math.round(
          closeDays.length % 2 === 0
            ? (closeDays[mid - 1] + closeDays[mid]) / 2
            : closeDays[mid],
        );

  // Aging keys off updated_at (the last status change): created_at would
  // mark a freshly-transitioned old row as aging, which it isn't.
  const agingCutoff = now.getTime() - AGING_CUTOFF_MS;
  const nonTerminal = new Set(stateSets.nonTerminalStates);
  const agingCount = rows.filter(
    (r) =>
      nonTerminal.has(r.status) &&
      new Date(r.updated_at).getTime() < agingCutoff,
  ).length;

  const pipeline = stateSets.happyPath.map((state, idx) => {
    const reach = reachable[state] ?? new Set<string>([state]);
    return {
      state,
      label: shortLabel(state),
      count: rows.filter((r) => reach.has(r.status)).length,
      reach: Array.from(reach),
      terminal: idx === stateSets.happyPath.length - 1,
    };
  });

  // Workflow order, so By Status reads top-to-bottom like the pipeline.
  const statusOrder = workflow?.states
    ? Object.keys(workflow.states)
    : Array.from(statusCounts.keys());
  const byStatus = statusOrder
    .map((status) => ({
      status,
      label: statusLabel(workflow, status) || status,
      count: byStatusCount(status),
    }))
    .filter((r) => r.count > 0);

  const byDepartment = countBy(rows, (r) => r.department);
  const committee = buildAnnualSscSummary(
    rows,
    buildReportPredicates(workflow, reachable),
    unitByDepartment,
    now,
  );

  return {
    total,
    pendingReview: stateSets.initialState
      ? byStatusCount(stateSets.initialState)
      : 0,
    closed,
    rejected,
    closureRate: total > 0 ? Math.round((closed * 100) / total) : 0,
    rejectRate: total > 0 ? Math.round((rejected * 100) / total) : 0,
    medianCloseDays,
    agingCount,
    stateSets,
    pipeline,
    rejections: stateSets.rejectedStates.map((status) => ({
      status,
      label: shortLabel(status),
      count: byStatusCount(status),
    })),
    byStatus,
    byCategory: countBy(rows, (r) => r.category),
    byDepartment,
    topDepartment: byDepartment[0],
    byReward: REWARD_CATEGORIES.map((key) => ({
      key,
      count: committee.byRewardCategory[key] ?? 0,
    })).filter((r) => r.count > 0),
    rewardTotal: Object.values(committee.byRewardCategory).reduce(
      (a, b) => a + b,
      0,
    ),
    committee,
  };
}

/**
 * Submissions trend over the filtered period (or earliest row → now for
 * All Time), auto-tiered by span so the chart stays readable (CLAUDE.md §10).
 */
export function buildTrend(
  rows: Suggestion[],
  date_from: string | undefined,
  date_to: string | undefined,
  now: Date = new Date(),
): { buckets: TrendBucket[]; granularity: TrendGranularity } {
  const earliest = rows.length
    ? new Date(Math.min(...rows.map((r) => new Date(r.created_at).getTime())))
    : now;
  const from = date_from ? new Date(date_from) : earliest;
  const to = date_to ? new Date(date_to) : now;
  const days = Math.max(
    1,
    Math.round((to.getTime() - from.getTime()) / 86400000),
  );
  const granularity = pickTrendGranularity(days);
  if (rows.length === 0) return { buckets: [], granularity };
  return { buckets: bucketRange(rows, from, to, granularity), granularity };
}

// ─── Print route scope ────────────────────────────────────────────────
// The Print button hands the Overview's whole filter set to
// /overview/print, which parses it back with parseOverviewPrintParams and
// lists the same rows. Multi-valued filters repeat their key rather than
// comma-joining, so a department name containing a comma survives.

const PRINT_MULTI_KEYS = [
  "status",
  "category",
  "department",
  "unit",
  "reward_category",
] as const;

export function buildOverviewPrintUrl(filters: SuggestionListFilters): string {
  const sp = new URLSearchParams();
  if (filters.date_from) sp.set("date_from", filters.date_from);
  if (filters.date_to) sp.set("date_to", filters.date_to);
  for (const key of PRINT_MULTI_KEYS) {
    (filters[key] ?? []).forEach((v) => sp.append(key, v));
  }
  if (filters.my_suggestions) sp.set("mine", "1");
  const qs = sp.toString();
  return qs ? `/overview/print?${qs}` : "/overview/print";
}

export function parseOverviewPrintParams(
  params: URLSearchParams,
): SuggestionListFilters {
  const filters: SuggestionListFilters = {};
  const date_from = params.get("date_from");
  const date_to = params.get("date_to");
  if (date_from) filters.date_from = date_from;
  if (date_to) filters.date_to = date_to;
  for (const key of PRINT_MULTI_KEYS) {
    const values = params.getAll(key).filter(Boolean);
    if (values.length) filters[key] = values;
  }
  if (params.get("mine") === "1") filters.my_suggestions = true;
  return filters;
}

/** "Label: a, b" lines naming every active non-date filter, for the print header. */
export function describeReportScope(
  filters: SuggestionListFilters,
  workflow: WorkflowConfig | undefined,
): string[] {
  const lines: string[] = [];
  const add = (label: string, values: string[] | undefined) => {
    if (values && values.length) lines.push(`${label}: ${values.join(", ")}`);
  };
  add(
    "Status",
    filters.status?.map((s) => statusShortLabel(workflow, s) || s),
  );
  add("Category", filters.category);
  add("Department", filters.department?.map((d) => displayDepartment(d)));
  add("Unit", filters.unit);
  add("Reward category", filters.reward_category);
  if (filters.my_suggestions) lines.push("Only my suggestions");
  return lines;
}
